<template>
  <div class="analysis-view">
    <div class="analysis-view__list">
      <h1>{{ t('measurement.measurements') }}</h1>
      <MeasurementList />
    </div>

    <div class="analysis-view__content">
      <TitleBar :title="t('measurement.currentPosition')">
        <BfButton
          :type="ButtonType.Outlined"
          icon="/icons/52-Arrows-Diagrams/01-Arrows/arrow-left-1.svg"
          @click="changeHardware"
        >
          {{ t('measurement.changeHardware') }}
        </BfButton>
        <BfButton
          :state="
            measurements.length <= 10 &&
            isCalibrated &&
            isLatestMeasurementCalibrated
              ? ButtonState.Enabled
              : ButtonState.Disabled
          "
          :type="ButtonType.Outlined"
          @click="addMeasurement"
        >
          {{ t('measurement.addMeasurement') }}
        </BfButton>
        <BfButton
          :state="
            measurements.length > 2 ? ButtonState.Enabled : ButtonState.Disabled
          "
          :icon-position="ButtonIconPosition.Right"
          icon="/icons/52-Arrows-Diagrams/01-Arrows/arrow-right-1.svg"
          @click="next"
        >
          {{ t('common.next') }}
        </BfButton>

        <template #measurements>
          <BfFlex>
            <BfMeasurement
              v-if="heartRateValue"
              :value="heartRateValue"
              :label="heartRate.unit"
              icon="/icons/measurements/heart.svg"
            />

            <BfMeasurement
              :value="instantaneousPowerValue"
              :label="instantaneousPower.unit"
              icon="/icons/measurements/wattage.svg"
            />

            <BfMeasurement
              :value="instantaneousCadenceValue"
              :label="instantaneousCadence.unit"
              icon="/icons/measurements/rpm.svg"
            />
          </BfFlex>
        </template>
      </TitleBar>

      <CurrentSetup />

      <BikeIllustration />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useMeasurementStore } from '@/store/measurementStore'
import { usePositionStore } from '@/store/positionStore'
import { useMqttStore } from '@/store/mqttStore'
import { storeToRefs } from 'pinia'
import TitleBar from '@/components/layout/TitleBar.vue'
import MeasurementList from '@/components/measurement/MeasurementList.vue'
import CurrentSetup from '@/components/measurement/CurrentSetup.vue'
import BikeIllustration from '@/components/preview/common/BikeIllustration.vue'
import {
  heartRate,
  instantaneousPower,
  instantaneousCadence,
} from '@/mqttDefinitions'
import {
  BfButton,
  BfFlex,
  BfMeasurement,
  ButtonIconPosition,
  ButtonState,
  ButtonType,
} from '@finetic/bikefitting-ui'
import { useI18n } from 'vue-i18n'
import { MqttTopic } from '@/mqttDefinitions/topics'

const { t } = useI18n()
const router = useRouter()

const measurementStore = useMeasurementStore()
const positionStore = usePositionStore()
const { absolutePosition, isCalibrated, isLatestMeasurementCalibrated } =
  storeToRefs(positionStore)
const { measurements, selectedMeasurement } = storeToRefs(measurementStore)

const changeHardware = () => {
  router.push({ name: 'setup' })
}

const addMeasurement = () => {
  measurementStore.create({
    crankset: {
      active: selectedMeasurement.value?.crankset.active,
    },
    handlebar: {
      active: selectedMeasurement.value?.handlebar.active,
      x: absolutePosition.value.handlebarX,
      y: absolutePosition.value.handlebarY,
    },
    saddle: {
      active: selectedMeasurement.value?.saddle.active,
      x: absolutePosition.value.saddleX,
      y: absolutePosition.value.saddleY,
    },
    pedal: {
      active: selectedMeasurement.value?.pedal.active,
    },
  })
}

const mqttStore = useMqttStore()
const { getMessageOnTopic, getRawValueOnTopic } = storeToRefs(mqttStore)

const heartRateValue = computed(() => getMessageOnTopic.value(heartRate))

const instantaneousPowerValue = computed(() =>
  getMessageOnTopic.value(instantaneousPower),
)

const instantaneousCadenceValue = computed(() => {
  const rawValue = getRawValueOnTopic.value(
    MqttTopic.RESISTANCE_INSTANTANEOUS_CADENCE,
  )

  if (rawValue && typeof rawValue === 'object' && 'value' in rawValue) {
    return 0
  } else {
    return Number(rawValue)?.toString() === 'NaN'
      ? 0
      : Math.floor(Number(rawValue))
  }
})

const next = () => {
  router.push({ name: 'data' })
}
</script>

<style lang="scss">
.analysis-view {
  display: flex;
  height: 100%;

  &__list {
    flex: 1 0 40%;
    background-color: #f5f5f5;
    padding: 2.4rem;
    overflow-y: auto;
    scrollbar-gutter: stable;
  }

  &__content {
    flex: 1 0 60%;
    background-color: #fbfbfb;
    padding: 2.4rem;
    overflow-y: auto;
  }
}
</style>
